import React, { useMemo } from 'react';
import { PAGE_WIDTH, PAGE_HEIGHT } from '@/types/yearbook';
import { TrombiTemplate, TrombiDisposition, TrombiTitlePosition, TrombiObjectPosition, TrombiAdditionalObject } from './TrombiPlaceDialog';
import { ensureFontLoaded } from '@/lib/fonts';

const PREVIEW_WIDTH = 200;
const PREVIEW_HEIGHT = PREVIEW_WIDTH * (PAGE_HEIGHT / PAGE_WIDTH);

// 5mm bleed in pt: background objects extend into this invisible area.
const BLEED_PT = 14.17;
const BLEED_RATIO_X = BLEED_PT / PAGE_WIDTH;
const BLEED_RATIO_Y = BLEED_PT / PAGE_HEIGHT;

interface TrombiPreviewProps {
  template?: TrombiTemplate;
}

const parseZ = (z?: number | string): number | undefined => {
  if (z === undefined || z === null || z === '') return undefined;
  const v = typeof z === 'string' ? parseInt(z, 10) : z;
  return Number.isNaN(v) ? undefined : v;
};

const TrombiPreview: React.FC<TrombiPreviewProps> = ({ template }) => {
  const scale = PREVIEW_WIDTH / PAGE_WIDTH;

  // Get dispositions array - hooks must be before any return
  const dispositions: TrombiDisposition[] = useMemo(() => {
    if (!template?.dispositions) return [];
    if (template.page_pair) {
      // Take the first element which is an array
      const first = template.dispositions[0];
      return Array.isArray(first) ? (first as TrombiDisposition[]) : [first as TrombiDisposition];
    }
    return template.dispositions as TrombiDisposition[];
  }, [template]);

  // Collect all additional objects with their z-layer (from dispositions + template level)
  const allAdditionalObjects = useMemo(() => {
    const backObjects: { obj: TrombiAdditionalObject; key: string }[] = [];
    const frontObjects: { obj: TrombiAdditionalObject; key: string }[] = [];
    const normalObjects: { obj: TrombiAdditionalObject; key: string }[] = [];

    const categorize = (obj: TrombiAdditionalObject, key: string) => {
      const item = { obj, key };
      if (obj.send_to_back) backObjects.push(item);
      else if (obj.bring_to_front) frontObjects.push(item);
      else normalObjects.push(item);
    };

    // Template-level additional objects
    (template?.additional_objects || template?.additionnal_objects)?.forEach((obj, j) => {
      categorize(obj, `tpl-add-${j}`);
    });

    // Disposition-level additional objects
    dispositions.forEach((dispo, i) => {
      (dispo.additional_objects || dispo.additionnal_objects)?.forEach((obj, j) => {
        categorize(obj, `add-${i}-${j}`);
      });
    });

    return { backObjects, normalObjects, frontObjects };
  }, [dispositions, template]);

  // Normalize objects_positions_name: if array of arrays, take first
  const objectsPositionsName: TrombiObjectPosition[] = useMemo(() => {
    const raw = template?.objects_positions_name;
    if (!raw || raw.length === 0) return [];
    if (Array.isArray(raw[0])) return raw[0] as TrombiObjectPosition[];
    return raw as TrombiObjectPosition[];
  }, [template]);

  // Normalize objects_positions_picture: if array of arrays, take first
  const objectsPositionsPicture: TrombiObjectPosition[] = useMemo(() => {
    const raw = template?.objects_positions_picture;
    if (!raw || raw.length === 0) return [];
    if (Array.isArray(raw[0])) return raw[0] as TrombiObjectPosition[];
    return raw as TrombiObjectPosition[];
  }, [template]);

  if (!template) {
    return (
      <div
        style={{
          width: '100%',
          aspectRatio: `${PAGE_WIDTH}/${PAGE_HEIGHT}`,
          backgroundColor: '#fff',
        }}
      />
    );
  }

  if (template.preview_img) {
    return (
      <img
        src={template.preview_img}
        alt={`Modèle ${template.id}`}
        className="w-full object-contain"
        style={{ aspectRatio: `${PAGE_WIDTH}/${PAGE_HEIGHT}` }}
        draggable={false}
      />
    );
  }

  const renderTitle = (title: TrombiTitlePosition | undefined) => {
    if (!title) return null;
    ensureFontLoaded(title.fontFamily);
    return (
      <div
        style={{
          position: 'absolute',
          left: title.x * scale,
          top: title.y * scale,
          width: title.width * scale,
          height: title.height * scale,
          fontSize: `${(parseFloat(title.fontSize || '12')) * scale}px`,
          fontFamily: title.fontFamily || 'sans-serif',
          textAlign: (title.justify === 'center' ? 'center' : title.justify === 'right' ? 'right' : 'left') as React.CSSProperties['textAlign'],
          display: 'flex',
          alignItems: 'center',
          justifyContent: title.justify === 'center' ? 'center' : title.justify === 'right' ? 'flex-end' : 'flex-start',
          overflow: 'hidden',
          color: title.color || '#333',
          backgroundColor: title.backgroundColor || 'transparent',
          lineHeight: 1.4,
          transform: title.angle ? `rotate(${title.angle}deg)` : undefined,
          transformOrigin: 'center center',
          zIndex: parseZ(title.zIndex) ?? 3,
        }}
      >
        {title.content}
      </div>
    );
  };

  const renderObjectPosition = (obj: TrombiObjectPosition, parentScale: number, key: string, dispoIndex: number) => {
    const objZ = parseZ(obj.zIndex);
    if (obj.object_type === 'picture') {
      const imgUrl = `https://zephir-prod.s3.eu-west-1.amazonaws.com/configuration/images/trombi_preview/${dispoIndex}.webp`;
      return (
        <img
          key={key}
          src={imgUrl}
          alt=""
          draggable={false}
          style={{
            position: 'absolute',
            left: obj.x * parentScale,
            top: obj.y * parentScale,
            width: obj.width * parentScale,
            height: obj.height * parentScale,
            objectFit: 'cover',
            borderRadius: obj.rounded ? '50%' : 2,
            transform: obj.angle ? `rotate(${obj.angle}deg)` : undefined,
            transformOrigin: 'center center',
            zIndex: objZ,
          }}
        />
      );
    }
    // text_template
    return (
      <div
        key={key}
        style={{
          position: 'absolute',
          left: obj.x * parentScale,
          top: obj.y * parentScale,
          width: obj.width * parentScale,
          height: obj.height * parentScale,
          fontSize: `${(parseFloat(obj.fontSize || '8')) * parentScale}px`,
          fontFamily: obj.fontFamily || 'sans-serif',
          textAlign: (obj.justify === 'center' ? 'center' : obj.justify === 'right' ? 'right' : 'left') as React.CSSProperties['textAlign'],
          display: 'flex',
          alignItems: 'center',
          justifyContent: obj.justify === 'center' ? 'center' : obj.justify === 'right' ? 'flex-end' : 'flex-start',
          overflow: 'hidden',
          color: obj.color || '#666',
          backgroundColor: obj.backgroundColor || 'transparent',
          lineHeight: 1.4,
          transform: obj.angle ? `rotate(${obj.angle}deg)` : undefined,
          transformOrigin: 'center center',
          zIndex: objZ,
        }}
      >
        {obj.content}
      </div>
    );
  };


  const renderAdditionalObject = (obj: TrombiAdditionalObject, key: string, defaultZ: number) => {
    const imgSrc = obj.thumbnail || obj.image_url;
    const zIndex = parseZ(obj.zIndex) ?? defaultZ;
    if (obj.object_type === 'background' || obj.object_type === 'background_color') {
      // Extend the background into the 5mm bleed so the visible page area is fully
      // covered and the background starts/ends in the clipped (non-visible) bleed zone.
      return (
        <div
          key={key}
          style={{
            position: 'absolute',
            left: `${-BLEED_RATIO_X * 100}%`,
            top: `${-BLEED_RATIO_Y * 100}%`,
            width: `${(1 + 2 * BLEED_RATIO_X) * 100}%`,
            height: `${(1 + 2 * BLEED_RATIO_Y) * 100}%`,
            backgroundColor: obj.color || undefined,
            backgroundImage: imgSrc ? `url(${imgSrc})` : undefined,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            pointerEvents: 'none',
            zIndex: parseZ(obj.zIndex) ?? 0,
          }}
        />
      );
    }
    if (obj.object_type === 'sticker' || obj.object_type === 'template_sticker') {

      return imgSrc ? (
        <img
          key={key}
          src={imgSrc}
          alt=""
          draggable={false}
          style={{
            position: 'absolute',
            left: obj.x * scale,
            top: obj.y * scale,
            width: obj.width * scale,
            height: obj.height * scale,
            objectFit: obj.force_dimension ? 'fill' : 'contain',
            transform: obj.angle ? `rotate(${obj.angle}deg)` : undefined,
            pointerEvents: 'none',
            zIndex,
          }}
        />
      ) : null;
    }
    if (obj.object_type === 'picture_template') {
      const ptUrl = obj.picture_template_url;
      return (
        <div
          key={key}
          style={{
            position: 'absolute',
            left: obj.x * scale,
            top: obj.y * scale,
            width: obj.width * scale,
            height: obj.height * scale,
            transform: obj.angle ? `rotate(${obj.angle}deg)` : undefined,
            overflow: 'hidden',
            pointerEvents: 'none',
            zIndex,
          }}
        >
          {ptUrl ? (
            <img
              src={ptUrl}
              alt=""
              draggable={false}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <div
              style={{
                width: '100%',
                height: '100%',
                backgroundColor: 'rgba(200,200,200,0.3)',
                border: '1px dashed rgba(150,150,150,0.5)',
              }}
            />
          )}
        </div>
      );
    }
    if (obj.object_type === 'shape' || obj.object_type === 'shape_border') {
      return (
        <div
          key={key}
          style={{
            position: 'absolute',
            left: obj.x * scale,
            top: obj.y * scale,
            width: obj.width * scale,
            height: obj.height * scale,
            backgroundColor: obj.object_type === 'shape' ? (obj.color || '#000') : 'transparent',
            border: obj.object_type === 'shape_border' ? `1px solid ${obj.color || '#000'}` : undefined,
            transform: obj.angle ? `rotate(${obj.angle}deg)` : undefined,
            pointerEvents: 'none',
            zIndex,
          }}
        />
      );
    }
    return null;
  };

  return (
    <div
      style={{
        width: '100%',
        aspectRatio: `${PAGE_WIDTH}/${PAGE_HEIGHT}`,
        backgroundColor: template.background_color || '#fff',
        backgroundImage: template.background?.image_url ? `url(${template.background.image_url})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Back layer: send_to_back objects */}
      {allAdditionalObjects.backObjects.map(({ obj, key }) => renderAdditionalObject(obj, key, 0))}

      {renderTitle(Array.isArray(template.title) ? template.title[0] : template.title)}
      {renderTitle(Array.isArray(template.title2) ? template.title2[0] : template.title2)}

      {dispositions.map((dispo, i) => {
        const dispoScale = scale;

        // Determine which objects_positions to use for this disposition
        let currentObjPositions = template.objects_positions;
        if (template.multi_objects_positions) {
          // Collect all available objects_positions variants (0 = objects_positions, 1 = objects_positions1, etc.)
          const variants: TrombiObjectPosition[][] = [template.objects_positions];
          for (let n = 1; ; n++) {
            const key = `objects_positions${n}` as keyof typeof template;
            const v = template[key] as TrombiObjectPosition[] | undefined;
            if (!v) break;
            variants.push(v);
          }
          if (variants.length > 1) {
            if (template.switch_index != null && template.switch_index > 0) {
              // Switch every switch_index elements
              const groupIndex = Math.floor(i / template.switch_index) % variants.length;
              currentObjPositions = variants[groupIndex];
            } else {
              // Each disposition cycles through variants one by one
              currentObjPositions = variants[i % variants.length];
            }
          }
        }

        return (
          <div
            key={i}
            style={{
              position: 'absolute',
              left: dispo.x * dispoScale,
              top: dispo.y * dispoScale,
              width: dispo.width * dispoScale,
              height: dispo.height * dispoScale,
              transform: dispo.angle ? `rotate(${dispo.angle}deg)` : undefined,
              backgroundColor: dispo.background_color || 'transparent',
              border: dispo.borderColor ? `${1 * dispoScale}px solid ${dispo.borderColor}` : undefined,
              boxShadow: dispo.shadow
                ? `${dispo.shadow.offsetX * dispoScale}px ${dispo.shadow.offsetY * dispoScale}px ${dispo.shadow.blur * dispoScale}px ${dispo.shadow.color}`
                : undefined,
              borderRadius: 2,
              zIndex: parseZ(dispo.zIndex) ?? 1,
            }}
          >
            {currentObjPositions?.map((obj, j) =>
              renderObjectPosition(obj, dispoScale, `${i}-${j}`, i)
            )}
          </div>
        );
      })}

      {/* Objects positions picture - absolute on page level, rendered once */}
      {objectsPositionsPicture.map((obj, j) =>
        renderObjectPosition(obj, scale, `pic-${j}`, j)
      )}

      {/* Objects positions name - absolute on page level, rendered once */}
      {objectsPositionsName.map((obj, j) =>
        renderObjectPosition(obj, scale, `name-${j}`, 0)
      )}

      {/* Normal layer */}
      {allAdditionalObjects.normalObjects.map(({ obj, key }) => renderAdditionalObject(obj, key, 2))}

      {/* Front layer: bring_to_front objects */}
      {allAdditionalObjects.frontObjects.map(({ obj, key }) => renderAdditionalObject(obj, key, 10))}
    </div>
  );
};

export default TrombiPreview;
